import { useEffect, useReducer } from "react";
import { elapsedMin } from "@/lib/format";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Play, Pause, RotateCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { TaskAdminRow } from "./TaskAdminActions";

function LiveTimer({ start, pauses }) {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const t = setInterval(force, 1000);
    return () => clearInterval(t);
  }, []);
  const m = elapsedMin(start, pauses);
  const mm = Math.floor(m);
  const ss = Math.floor((m - mm) * 60);
  return (
    <span className="metric text-3xl font-extrabold text-signal-amber" data-testid="task-live-timer">
      {String(Math.floor(mm / 60)).padStart(2, "0")}:{String(mm % 60).padStart(2, "0")}:{String(ss).padStart(2, "0")}
    </span>
  );
}

export function taskBorderClass(t) {
  if (t.status === "in_progress") return "border-signal-amber/50";
  if (t.deadline && new Date(t.deadline) < new Date()) return "border-signal-red/50";
  return "border-border";
}

export function resolveAssignmentLabel(t) {
  const names = t.assigned_to_names || [];
  if (names.length) return names.join(", ");
  return t.required_role ? `Any ${t.required_role} user` : "Unassigned";
}

export function TaskCard({ t, isWorker, canManage, isManager, userId, userRole, onNavigateBill, act, onAssign, onComplete, onProblem, onAdminAction, isAdmin }) {
  const assigned = (t.assigned_to || []).includes(userId);
  const roleAllowed = userRole === "admin" || (!!t.required_role && t.required_role === userRole);
  const canAct = isWorker || assigned || roleAllowed;
  const assignedNames = t.assigned_to_names || [];
  const assignmentLabel = resolveAssignmentLabel(t);

  return (
    <div className={`bg-card border rounded-lg p-4 space-y-3 ${taskBorderClass(t)}`} data-testid={`task-card-${t.task_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-heading font-bold text-lg uppercase">{t.type}</span>
            <StatusBadge status={t.status} />
            {t._pending && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-signal-amber/50 text-signal-amber" data-testid={`pending-sync-${t.task_id}`}>Pending sync</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            <span className="metric text-primary">{t.task_id}</span>
            {t.bill_no && <> · <button className="metric text-signal-blue underline" onClick={() => onNavigateBill(t.bill_no)} data-testid={`task-bill-link-${t.task_id}`}>{t.bill_no}</button></>}
            {t.sku && <> · <span className="metric text-signal-orange font-bold">{t.sku}</span></>}
            {t.quantity != null && <> · <span className="metric">{t.quantity.toLocaleString()}</span> pcs</>}
          </p>
          {t.location && <p className="text-xs text-muted-foreground font-mono mt-0.5">{t.location}</p>}
          {!isWorker && <p className="text-xs text-muted-foreground mt-0.5">Assigned: {assignmentLabel}</p>}
        </div>
        {t.status === "in_progress" && <LiveTimer start={t.start_time} pauses={t.pauses} />}
      </div>

      <div className={`grid gap-2 ${t.status === "in_progress" ? "grid-cols-2" : "grid-cols-1"}`}>
        {t.status === "assigned" && canManage && (
          <Button variant="outline" className="h-14 text-base font-semibold" onClick={() => onAssign(t)} data-testid={`assign-existing-${t.task_id}`}>
            ASSIGN {t.required_role ? t.required_role.toUpperCase() : "WORKERS"}{assignedNames.length > 0 ? ` (${assignedNames.join(", ")})` : ""}
          </Button>
        )}
        {t.status === "assigned" && canAct && (
          <Button className="h-14 text-lg font-bold bg-signal-green hover:bg-signal-green/90 text-white transition-colors duration-150" onClick={() => act(t.task_id, "start")} data-testid={`start-task-${t.task_id}`}>
            <Play className="w-5 h-5 mr-2" /> START
          </Button>
        )}
        {t.status === "in_progress" && canAct && (
          <>
            <Button className="h-14 text-lg font-bold bg-signal-amber hover:bg-signal-amber/90 text-white transition-colors duration-150" onClick={() => act(t.task_id, "pause")} data-testid={`pause-task-${t.task_id}`}>
              <Pause className="w-5 h-5 mr-2" /> PAUSE
            </Button>
            <Button className="h-14 text-lg font-bold bg-signal-blue hover:bg-signal-blue/90 text-white transition-colors duration-150" onClick={() => onComplete(t)} data-testid={`complete-task-${t.task_id}`}>
              <CheckCircle2 className="w-5 h-5 mr-2" /> COMPLETE
            </Button>
          </>
        )}
        {t.status === "paused" && canAct && (
          <Button className="h-14 text-lg font-bold bg-signal-green hover:bg-signal-green/90 text-white transition-colors duration-150" onClick={() => act(t.task_id, "resume")} data-testid={`resume-task-${t.task_id}`}>
            <RotateCw className="w-5 h-5 mr-2" /> RESUME
          </Button>
        )}
      </div>
      {t.status !== "completed" && t.status !== "cancelled" && t.status !== "voided" && (
        <Button variant="outline" className="w-full h-11 border-signal-red/40 text-signal-red hover:bg-signal-red/10 transition-colors duration-150" onClick={() => onProblem(t)} data-testid={`problem-task-${t.task_id}`}>
          <AlertTriangle className="w-4 h-4 mr-2" /> REPORT PROBLEM
        </Button>
      )}
      {t.overdue && <p className="text-[10px] font-mono uppercase tracking-widest text-signal-red font-bold" data-testid={`overdue-${t.task_id}`}>OVERDUE</p>}
      {onAdminAction && <TaskAdminRow task={t} isAdmin={isAdmin} isManager={isManager} canManage={canManage} onAction={onAdminAction} />}
    </div>
  );
}
